import { useEffect, useState } from 'react';
import { preloadSfx, SFX_COUNT, isSfxReady } from './sfx';
import { loadBattleAssets, battleAssetCount, isBattleAssetsReady } from './battleAssets';
import { formatApiError } from '../../../utils/apiError';

const MIN_LOADING_MS = 3000;

export interface BattlePreload {
  assetsReady: boolean;
  loadProgress: number;
  loadError: boolean;
  phraseIdx: number;
}

/**
 * Preload EVERYTHING (sprites + sounds) before showing the Rush Mid UI. Skips entirely if
 * already cached (assetsReady starts true); otherwise holds the loading screen for at least
 * MIN_LOADING_MS so it doesn't flicker, and drives a progress fraction + a rotating phrase.
 * The asset flood starts as soon as the gate mounts — the loading screen is a static image,
 * so there's nothing for the fetches to starve.
 */
export function useBattlePreload(): BattlePreload {
  const [assetsReady, setAssetsReady] = useState(() => isBattleAssetsReady() && isSfxReady());
  const [loadError, setLoadError] = useState(false);
  const [loadProgress, setLoadProgress] = useState(0); // 0..1 over sounds + sprites
  // random start so the loading phrases aren't always shown in the same order from #0
  const [phraseIdx, setPhraseIdx] = useState(() => Math.floor(Math.random() * 1_000_000));

  useEffect(() => {
    if (assetsReady) return;
    let cancelled = false;
    const startedAt = Date.now();
    const total = SFX_COUNT + battleAssetCount();
    // Sprites already warm from an earlier visit don't re-fire their per-unit tick — loadShared
    // hands back a cached promise and only its FIRST caller's onUnit runs — so seed `done` with
    // them up front, or the bar stalls at the sound-only fraction then jumps. (Sounds always tick:
    // loadSounds fires onUnit per name regardless of cache, so they must NOT be seeded.)
    let done = isBattleAssetsReady() ? battleAssetCount() : 0;
    setLoadProgress(total ? done / total : 1);
    const tick = () => { if (!cancelled) { done += 1; setLoadProgress(Math.min(1, done / total)); } };

    Promise.all([preloadSfx(tick), loadBattleAssets(undefined, tick)])
      .then(() => {
        if (!cancelled) setLoadProgress(1); // never leave the bar short of 100% before we advance
        const wait = Math.max(0, MIN_LOADING_MS - (Date.now() - startedAt));
        window.setTimeout(() => { if (!cancelled) setAssetsReady(true); }, wait);
      })
      .catch((err) => {
        // Причина уходит в консоль, а не на экран: она на английском и написана для
        // разработчика. Пользователь видит переведённый текст экрана загрузки.
        console.error('Rush Mid: failed to preload battle assets:', formatApiError(err));
        if (!cancelled) setLoadError(true);
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // cycle the playful loading phrase every ~2.2s while assets load — a random stride
  // (never 0) so the phrases feel shuffled, not a fixed A→B→C loop.
  useEffect(() => {
    if (assetsReady) return;
    const id = window.setInterval(() => setPhraseIdx((i) => i + 1 + Math.floor(Math.random() * 3)), 2200);
    return () => window.clearInterval(id);
  }, [assetsReady]);

  return { assetsReady, loadProgress, loadError, phraseIdx };
}
